import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Chip,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import {
  WhatsApp,
  CheckCircle,
  QrCode2,
  Store,
  TwoWheeler,
  DirectionsCar,
  Done,
  DoneAll,
  LocalOffer,
  Event,
  TrendingUp,
  Verified,
  Search,
  ShoppingBag,
  ChevronRight,
  SportsBasketball,
  Checkroom,
  LocalCafe,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import AnimatedSection from "../AnimatedSection";
import { gradients } from "../../../utils/theme";

/**
 * Chat Message Interface
 */
interface ChatMessage {
  id: number;
  sender: "user" | "bot";
  text?: string;
  type?:
    | "text"
    | "buttons"
    | "flow"
    | "payment"
    | "upi-selection"
    | "upi-pin"
    | "success"
    | "qr"
    | "link"
    | "feedback-prompt"
    | "feedback-form"
    | "feedback-thanks";
  buttons?: string[];
  flowData?: { mall?: string; vehicle?: string; amount?: string };
  delay: number;
  status?: "sent" | "delivered" | "read";
}

/**
 * WhatsApp Chat Preview Component
 *
 * Shows complete user journey: Greeting → Parking Booking → Payment → QR Ticket → Digital Mall
 */
const WhatsAppChatPreview: React.FC = () => {
  const [visibleMessages, setVisibleMessages] = useState<number[]>([]);
  const [showUpiModal, setShowUpiModal] = useState(false);
  const [upiStep, setUpiStep] = useState<
    "apps" | "confirm" | "pin" | "success"
  >("apps");
  const [selectedUpiApp, setSelectedUpiApp] = useState<string>("");
  const [pinDigits, setPinDigits] = useState<number[]>([]);
  const [feedbackRating, setFeedbackRating] = useState<number>(0);
  const [feedbackComment, setFeedbackComment] = useState<string>("");
  const [feedbackButtonClicked, setFeedbackButtonClicked] = useState(false);
  const [messageStatuses, setMessageStatuses] = useState<
    Record<number, "sent" | "delivered" | "read">
  >({
    0: "read",
    1: "read",
    2: "read",
    3: "read",
    4: "read",
    5: "read",
    6: "read",
    7: "read",
    8: "read",
    9: "read",
    10: "read",
    11: "read",
    12: "read",
    13: "read",
    14: "read",
    15: "read",
    16: "read",
    17: "read",
  });
  const [showTyping, setShowTyping] = useState(false);
  const [showMallHub, setShowMallHub] = useState(false);
  const [exploreButtonClicked, setExploreButtonClicked] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [animationCycle, setAnimationCycle] = useState(0);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const mallHubRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

  // UPI Apps data
  const upiApps = [
    { name: "Google Pay", color: "#4285F4", logo: "G" },
    { name: "PhonePe", color: "#5F259F", logo: "P" },
    { name: "Paytm", color: "#00BAF2", logo: "₹" },
    { name: "BHIM", color: "#FF6B00", logo: "B" },
  ];

  // Complete user journey messages
  const messages: ChatMessage[] = useMemo(
    () => [
      { id: 1, sender: "user", type: "text", text: "Hi", delay: 800 },
      {
        id: 2,
        sender: "bot",
        type: "buttons",
        text: "Hi! Welcome to MallInHand 👋\n\nHow can I help you today?",
        buttons: ["🏢 Explore Malls", "🅿️ Buy Parking Ticket"],
        delay: 2500,
      },
      {
        id: 3,
        sender: "user",
        type: "text",
        text: "🅿️ Buy Parking Ticket",
        delay: 5000,
      },
      {
        id: 4,
        sender: "bot",
        type: "flow",
        text: "Great! Please fill in the details:",
        delay: 6500,
      },
      {
        id: 5,
        sender: "user",
        type: "text",
        text: "Galaxy Mall, Car - ₹30",
        delay: 10000,
      },
      {
        id: 6,
        sender: "bot",
        type: "payment",
        text: "🎫 Parking Booking\n\nMall: Galaxy Mall\nVehicle: Car\nAmount: ₹30",
        delay: 11500,
      },
      {
        id: 13,
        sender: "bot",
        type: "qr",
        text: "🎫 Your Parking Ticket",
        delay: 25000,
      },
      {
        id: 14,
        sender: "bot",
        type: "link",
        text: "🏢 Explore Galaxy Mall digitally!\n\nDiscover stores, offers, and events.",
        delay: 27500,
      },
      {
        id: 15,
        sender: "bot",
        type: "feedback-prompt",
        text: "We hope you enjoy your mall experience! 😊\n\nPlease share your feedback after visit?",
        delay: 43000,
      },
      {
        id: 16,
        sender: "user",
        type: "text",
        text: "✅ Share Feedback",
        delay: 44500,
      },
      {
        id: 17,
        sender: "bot",
        type: "feedback-form",
        text: "Please rate your mall experience and share your thoughts:",
        delay: 46000,
      },
      {
        id: 18,
        sender: "bot",
        type: "feedback-thanks",
        text: "Thank you for your valuable feedback! 🙏\n\nYour input helps us serve you better. Have a great day!",
        delay: 50500,
      },
    ],
    [],
  );

  // Intersection Observer - Start animation only when in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
        if (!entry.isIntersecting) {
          // Reset when out of view to save performance
          timeoutsRef.current.forEach(clearTimeout);
          timeoutsRef.current = [];
          setVisibleMessages([]);
          setMessageStatuses({});
          setShowTyping(false);
          setShowMallHub(false);
          setShowUpiModal(false);
          setExploreButtonClicked(false);
          setFeedbackButtonClicked(false);
          setFeedbackRating(0);
          setFeedbackComment("");
          setAnimationCycle(0);
        }
      },
      { threshold: 0.3 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);

      // Check if already in view on mount (fixes refresh issue)
      // Use setTimeout to ensure DOM is fully rendered
      setTimeout(() => {
        if (!sectionRef.current) return;

        const rect = sectionRef.current.getBoundingClientRect();
        const windowHeight =
          window.innerHeight || document.documentElement.clientHeight;

        // Check if at least 30% of element is visible (matching threshold: 0.3)
        const visibleHeight =
          Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0);
        const elementHeight = rect.height;
        const visibleRatio = visibleHeight / elementHeight;

        if (visibleRatio >= 0.3 && !isInView) {
          console.log("WhatsApp section initially visible, starting animation");
          setIsInView(true);
        }
      }, 100); // Small delay to ensure render is complete
    }

    return () => observer.disconnect();
  }, [isInView]);

  // Auto-scroll to bottom when new messages appear
  useEffect(() => {
    if (chatContainerRef.current && !showMallHub) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [visibleMessages, showTyping, showMallHub]);

  // Auto-scroll MallHub content (simple vertical scroll)
  useEffect(() => {
    if (showMallHub && mallHubRef.current) {
      const scrollHeight = mallHubRef.current.scrollHeight;
      const duration = 11000; // 11 seconds smooth scroll (slower for better visibility)
      const startTime = Date.now();

      const animateScroll = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        // Cubic ease in-out for smooth scroll
        const easeProgress =
          progress < 0.5
            ? 4 * progress * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 3) / 2;

        if (mallHubRef.current) {
          mallHubRef.current.scrollTop = scrollHeight * easeProgress;
        }

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };

      setTimeout(() => animateScroll(), 1000); // Start after 1s to let users see top content
    }
  }, [showMallHub]);

  // Auto-play messages and UPI flow - only when in view, loop infinitely
  useEffect(() => {
    if (!isInView) return;

    // Clear previous timeouts
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];

    setVisibleMessages([]);
    setMessageStatuses({});
    setShowTyping(false);
    setShowMallHub(false);
    setShowUpiModal(false);
    setExploreButtonClicked(false);
    setFeedbackButtonClicked(false);
    setFeedbackRating(0);
    setFeedbackComment("");
    setPinDigits([]);

    messages.forEach((message, index) => {
      // Show typing before bot messages
      if (message.sender === "bot" && index > 0) {
        const typingTimeout = setTimeout(() => {
          setShowTyping(true);
        }, message.delay - 1200);
        timeoutsRef.current.push(typingTimeout);
      }

      // Show message
      const timeout = setTimeout(() => {
        setShowTyping(false);
        setVisibleMessages((prev) => [...prev, message.id]);

        // Set initial status for user messages
        if (message.sender === "user") {
          setMessageStatuses((prev) => ({ ...prev, [message.id]: "sent" }));

          // Delivered after 300ms
          const deliveredTimeout = setTimeout(() => {
            setMessageStatuses((prev) => ({
              ...prev,
              [message.id]: "delivered",
            }));
          }, message.delay + 200);
          timeoutsRef.current.push(deliveredTimeout);

          // Read after 600ms
          const readTimeout = setTimeout(() => {
            setMessageStatuses((prev) => ({ ...prev, [message.id]: "read" }));
          }, message.delay + 400);
          timeoutsRef.current.push(readTimeout);
        }
      }, message.delay);
      timeoutsRef.current.push(timeout);
    });

    // Trigger UPI modal after Pay button appears
    const upiModalTimeout = setTimeout(() => {
      setShowUpiModal(true);
      setUpiStep("apps");
    }, 13500);
    timeoutsRef.current.push(upiModalTimeout);

    // Select UPI app
    const selectAppTimeout = setTimeout(() => {
      setSelectedUpiApp("Google Pay");
      setUpiStep("confirm");
    }, 15500);
    timeoutsRef.current.push(selectAppTimeout);

    // Show PIN entry
    const pinTimeout = setTimeout(() => {
      setUpiStep("pin");
      setPinDigits([]);
    }, 17500);
    timeoutsRef.current.push(pinTimeout);

    // Enter PIN digits one by one
    [1, 2, 3, 4].forEach((digit, index) => {
      const digitTimeout = setTimeout(
        () => {
          setPinDigits((prev) => [...prev, digit]);
        },
        18500 + index * 500,
      );
      timeoutsRef.current.push(digitTimeout);
    });

    // Show success
    const successTimeout = setTimeout(() => {
      setUpiStep("success");
    }, 21000);
    timeoutsRef.current.push(successTimeout);

    // Close modal and show QR
    const closeModalTimeout = setTimeout(() => {
      setShowUpiModal(false);
      setUpiStep("apps");
      setPinDigits([]);
    }, 23500);
    timeoutsRef.current.push(closeModalTimeout);

    // Simulate click on Explore button
    const clickExploreTimeout = setTimeout(() => {
      setExploreButtonClicked(true);
    }, 29000);
    timeoutsRef.current.push(clickExploreTimeout);

    // Auto-open MallHub after click animation
    const openMallHubTimeout = setTimeout(() => {
      setShowMallHub(true);
      setExploreButtonClicked(false);
    }, 29500);
    timeoutsRef.current.push(openMallHubTimeout);

    // Close MallHub and restart loop
    const closeMallHubTimeout = setTimeout(() => {
      setShowMallHub(false);
    }, 41500);
    timeoutsRef.current.push(closeMallHubTimeout);

    // Click feedback button
    const feedbackClickTimeout = setTimeout(() => {
      setFeedbackButtonClicked(true);
    }, 42500);
    timeoutsRef.current.push(feedbackClickTimeout);

    // Simulate feedback form fill and submit
    const fillFeedbackTimeout = setTimeout(() => {
      setFeedbackButtonClicked(false);
      setFeedbackRating(5);
    }, 46000);
    timeoutsRef.current.push(fillFeedbackTimeout);

    const fillCommentTimeout = setTimeout(() => {
      setFeedbackComment("Great experience! Very convenient.");
    }, 47000);
    timeoutsRef.current.push(fillCommentTimeout);

    // Restart entire animation loop
    const restartTimeout = setTimeout(() => {
      setFeedbackRating(0);
      setFeedbackComment("");
      setAnimationCycle((prev) => prev + 1);
    }, 51000);
    timeoutsRef.current.push(restartTimeout);

    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, [messages, isInView, animationCycle]);

  return (
    <Box
      id="whatsapp-journey"
      ref={sectionRef}
      sx={{
        py: { xs: 4, sm: 5, md: 6, lg: 8 },
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative background elements */}
      <Box
        sx={{
          position: "absolute",
          top: "-10%",
          right: "-5%",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background: gradients.gold,
          opacity: 0.1,
          filter: "blur(80px)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "-10%",
          left: "-5%",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background: gradients.navyLux,
          opacity: 0.1,
          filter: "blur(80px)",
        }}
      />

      <Container maxWidth="lg">
        <AnimatedSection>
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Chip
              icon={<WhatsApp />}
              label="WhatsApp Experience"
              sx={{
                mb: 2,
                px: 2,
                py: 0.5,
                fontSize: "0.875rem",
                fontWeight: 600,
                background: gradients.gold,
                color: "#1a237e",
                "& .MuiChip-icon": { color: "#1a237e" },
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "1.75rem", md: "2.5rem" },
                fontWeight: 700,
                mb: 2,
              }}
            >
              Complete Journey on WhatsApp
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "text.secondary",
                fontWeight: 400,
                maxWidth: "700px",
                mx: "auto",
                fontSize: { xs: "1rem", md: "1.25rem" },
                px: { xs: 2, md: 0 },
              }}
            >
              From greeting to parking payment to exploring malls - all in one
              seamless conversation
            </Typography>
          </Box>
        </AnimatedSection>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 4, sm: 5, md: 6 },
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Phone Mockup */}
          <AnimatedSection delay={0.2}>
            <Box
              sx={{
                position: "relative",
                width: { xs: "340px", sm: "340px", md: "360px" },
                maxWidth: { xs: "90%", sm: 360 },
                height: { xs: "580px", sm: "620px", md: "520px", lg: "640px" },
                mx: "auto",
              }}
            >
              {/* Phone Frame */}
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  borderRadius: { xs: 5, md: 6 },
                  background: "#000",
                  p: { xs: 1, md: 1.5 },
                  boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                }}
              >
                {/* Phone Screen */}
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    borderRadius: { xs: 4, md: 5 },
                    background: "#ece5dd",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                  }}
                >
                  {/* WhatsApp Header */}
                  <Box
                    sx={{
                      background: "#075e54",
                      color: "white",
                      p: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        background: gradients.gold,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#1a237e",
                        overflow: "hidden",
                      }}
                    >
                      <ShoppingBag sx={{ fontSize: 20 }} />
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.2 }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ fontSize: "0.9rem" }}
                        >
                          MallInHand
                        </Typography>
                        <Verified sx={{ fontSize: 14, color: "#25D366" }} />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{ opacity: 0.8, fontSize: "0.7rem" }}
                      >
                        online
                      </Typography>
                    </Box>
                  </Box>

                  {/* Chat Messages */}
                  <Box
                    ref={chatContainerRef}
                    sx={{
                      flex: 1,
                      p: 2,
                      overflowY: "auto",
                      overflowX: "hidden",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                      scrollBehavior: "smooth",
                      "&::-webkit-scrollbar": { width: 6 },
                      "&::-webkit-scrollbar-thumb": {
                        background: "rgba(0,0,0,0.2)",
                        borderRadius: 3,
                      },
                    }}
                  >
                    <AnimatePresence>
                      {messages.map((message) => {
                        if (!visibleMessages.includes(message.id)) return null;

                        return (
                          <motion.div
                            key={message.id}
                            initial={{ opacity: 0, y: 20, scale: 0.8 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            transition={{ duration: 0.3, ease: "easeOut" }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent:
                                  message.sender === "user"
                                    ? "flex-end"
                                    : "flex-start",
                              }}
                            >
                              {/* Text Message */}
                              {message.type === "text" && (
                                <Box
                                  sx={{
                                    maxWidth: "75%",
                                    p: 1.5,
                                    borderRadius: 2,
                                    background:
                                      message.sender === "user"
                                        ? "#dcf8c6"
                                        : "white",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                    whiteSpace: "pre-line",
                                    position: "relative",
                                    paddingRight:
                                      message.sender === "user"
                                        ? "28px"
                                        : "12px",
                                    willChange: "transform",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ fontSize: "0.875rem" }}
                                  >
                                    {message.text}
                                  </Typography>
                                  {message.sender === "user" && (
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        bottom: 6,
                                        right: 6,
                                        display: "flex",
                                        alignItems: "center",
                                        width: 16,
                                        height: 14,
                                      }}
                                    >
                                      {/* All ticks always rendered, visibility controlled by opacity */}
                                      <Done
                                        sx={{
                                          fontSize: 14,
                                          color: "rgba(0,0,0,0.45)",
                                          position: "absolute",
                                          opacity:
                                            messageStatuses[message.id] ===
                                            "sent"
                                              ? 1
                                              : 0,
                                          transition: "opacity 0.15s ease",
                                        }}
                                      />
                                      <DoneAll
                                        sx={{
                                          fontSize: 14,
                                          color:
                                            messageStatuses[message.id] ===
                                            "read"
                                              ? "#4fc3f7"
                                              : "rgba(0,0,0,0.45)",
                                          position: "absolute",
                                          opacity:
                                            messageStatuses[message.id] ===
                                              "delivered" ||
                                            messageStatuses[message.id] ===
                                              "read"
                                              ? 1
                                              : 0,
                                          transition:
                                            "opacity 0.15s ease, color 0.15s ease",
                                        }}
                                      />
                                    </Box>
                                  )}
                                </Box>
                              )}

                              {/* Buttons Message */}
                              {message.type === "buttons" && (
                                <Box sx={{ maxWidth: "85%", width: "100%" }}>
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      borderRadius: 2,
                                      background: "white",
                                      boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                      mb: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: "0.875rem",
                                        whiteSpace: "pre-line",
                                      }}
                                    >
                                      {message.text}
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 0.5,
                                    }}
                                  >
                                    {message.buttons?.map((btn, i) => (
                                      <Button
                                        key={i}
                                        variant="outlined"
                                        fullWidth
                                        sx={{
                                          textTransform: "none",
                                          color: "#075e54",
                                          borderColor: "#075e54",
                                          fontSize: "0.875rem",
                                          py: 1,
                                          transition: "all 0.2s ease",
                                          "&:hover": {
                                            background: "rgba(7, 94, 84, 0.05)",
                                            borderColor: "#075e54",
                                          },
                                          "&:active": {
                                            transform: "scale(0.98)",
                                            background: "rgba(7, 94, 84, 0.1)",
                                          },
                                        }}
                                      >
                                        {btn}
                                      </Button>
                                    ))}
                                  </Box>
                                </Box>
                              )}

                              {/* WhatsApp Flow Message */}
                              {message.type === "flow" && (
                                <Box sx={{ maxWidth: "85%", width: "100%" }}>
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      borderRadius: 2,
                                      background: "white",
                                      boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                      mb: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{ fontSize: "0.875rem", mb: 1.5 }}
                                    >
                                      {message.text}
                                    </Typography>
                                    <Box
                                      sx={{
                                        background: "#f0f0f0",
                                        p: 2,
                                        borderRadius: 2,
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        fontWeight={600}
                                        sx={{ mb: 1, display: "block" }}
                                      >
                                        Select Mall
                                      </Typography>
                                      <RadioGroup
                                        value="phoenix"
                                        sx={{ mb: 2 }}
                                      >
                                        <FormControlLabel
                                          value="phoenix"
                                          control={<Radio size="small" />}
                                          label={
                                            <Typography variant="caption">
                                              Galaxy Mall
                                            </Typography>
                                          }
                                        />
                                        <FormControlLabel
                                          value="orion"
                                          control={<Radio size="small" />}
                                          label={
                                            <Typography variant="caption">
                                              Orion Mall
                                            </Typography>
                                          }
                                        />
                                      </RadioGroup>
                                      <Typography
                                        variant="caption"
                                        fontWeight={600}
                                        sx={{ mb: 1, display: "block" }}
                                      >
                                        Select Vehicle
                                      </Typography>
                                      <RadioGroup value="car" sx={{ mb: 2 }}>
                                        <FormControlLabel
                                          value="bike"
                                          control={<Radio size="small" />}
                                          label={
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                              }}
                                            >
                                              <TwoWheeler
                                                sx={{ fontSize: 16 }}
                                              />
                                              <Typography variant="caption">
                                                Bike - ₹20
                                              </Typography>
                                            </Box>
                                          }
                                        />
                                        <FormControlLabel
                                          value="car"
                                          control={<Radio size="small" />}
                                          label={
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                              }}
                                            >
                                              <DirectionsCar
                                                sx={{ fontSize: 16 }}
                                              />
                                              <Typography variant="caption">
                                                Car - ₹30
                                              </Typography>
                                            </Box>
                                          }
                                        />
                                      </RadioGroup>
                                      <Button
                                        fullWidth
                                        variant="contained"
                                        size="small"
                                        sx={{
                                          background: "#075e54",
                                          textTransform: "none",
                                          fontSize: "0.75rem",
                                          transition: "all 0.2s ease",
                                          "&:hover": {
                                            background: "#064239",
                                          },
                                          "&:active": {
                                            transform: "scale(0.98)",
                                          },
                                        }}
                                      >
                                        Send
                                      </Button>
                                    </Box>
                                  </Box>
                                </Box>
                              )}

                              {/* Payment Order Message */}
                              {message.type === "payment" && (
                                <Box sx={{ maxWidth: "85%", width: "100%" }}>
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      borderRadius: 2,
                                      background: "white",
                                      boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: "0.875rem",
                                        whiteSpace: "pre-line",
                                        mb: 2,
                                      }}
                                    >
                                      {message.text}
                                    </Typography>
                                    <Button
                                      fullWidth
                                      variant="contained"
                                      size="small"
                                      sx={{
                                        background: "#075e54",
                                        textTransform: "none",
                                        fontSize: "0.75rem",
                                        transition: "all 0.2s ease",
                                        "&:hover": {
                                          background: "#064239",
                                        },
                                        "&:active": {
                                          transform: "scale(0.98)",
                                        },
                                      }}
                                    >
                                      Pay ₹30
                                    </Button>
                                  </Box>
                                </Box>
                              )}

                              {/* QR Ticket */}
                              {message.type === "qr" && (
                                <Box sx={{ maxWidth: "85%", width: "100%" }}>
                                  <Box
                                    sx={{
                                      p: 2,
                                      borderRadius: 2,
                                      background: "white",
                                      boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                      textAlign: "center",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      fontWeight={600}
                                      sx={{ mb: 1.5 }}
                                    >
                                      {message.text}
                                    </Typography>
                                    <Box
                                      sx={{
                                        p: 2,
                                        background: "#f8f9fa",
                                        borderRadius: 2,
                                        mb: 1.5,
                                      }}
                                    >
                                      <QrCode2
                                        sx={{ fontSize: 80, color: "#000" }}
                                      />
                                    </Box>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: "block",
                                        color: "text.secondary",
                                      }}
                                    >
                                      Galaxy Mall
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: "block",
                                        color: "text.secondary",
                                      }}
                                    >
                                      Scan it at the Entry Gate <br />
                                      Valid: Only for Today, Single Entry
                                    </Typography>
                                  </Box>
                                </Box>
                              )}

                              {/* Explore Button */}
                              {message.type === "link" && (
                                <Box sx={{ maxWidth: "85%", width: "100%" }}>
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      borderRadius: 2,
                                      background: "white",
                                      boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: "0.875rem",
                                        whiteSpace: "pre-line",
                                        mb: 1.5,
                                      }}
                                    >
                                      {message.text}
                                    </Typography>
                                    <motion.div
                                      animate={{
                                        scale: exploreButtonClicked
                                          ? [1, 0.95, 1]
                                          : 1,
                                      }}
                                      transition={{ duration: 0.3 }}
                                    >
                                      <Button
                                        fullWidth
                                        variant="contained"
                                        startIcon={<Store />}
                                        sx={{
                                          background: gradients.gold,
                                          color: "#1a237e",
                                          textTransform: "none",
                                          fontWeight: 600,
                                          fontSize: "0.875rem",
                                          opacity: exploreButtonClicked
                                            ? 0.7
                                            : 1,
                                          transition:
                                            "opacity 0.3s, transform 0.2s",
                                          "&:hover": {
                                            background: gradients.gold,
                                            opacity: 0.9,
                                          },
                                          "&:active": {
                                            transform: "scale(0.98)",
                                          },
                                        }}
                                      >
                                        Explore
                                      </Button>
                                    </motion.div>
                                  </Box>
                                </Box>
                              )}

                              {/* Feedback Prompt */}
                              {message.type === "feedback-prompt" && (
                                <Box sx={{ maxWidth: "85%", width: "100%" }}>
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      borderRadius: 2,
                                      background: "white",
                                      boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: "0.875rem",
                                        whiteSpace: "pre-line",
                                        mb: 1.5,
                                      }}
                                    >
                                      {message.text}
                                    </Typography>
                                    <motion.div
                                      animate={{
                                        scale: feedbackButtonClicked
                                          ? [1, 0.95, 1]
                                          : 1,
                                      }}
                                      transition={{ duration: 0.3 }}
                                    >
                                      <Button
                                        fullWidth
                                        variant="outlined"
                                        sx={{
                                          color: "#075e54",
                                          borderColor: "#075e54",
                                          textTransform: "none",
                                          fontSize: "0.875rem",
                                          py: 1,
                                          opacity: feedbackButtonClicked
                                            ? 0.7
                                            : 1,
                                          transition: "all 0.2s ease",
                                          "&:hover": {
                                            background: "rgba(7, 94, 84, 0.05)",
                                            borderColor: "#075e54",
                                          },
                                        }}
                                      >
                                        ✅ Share Feedback
                                      </Button>
                                    </motion.div>
                                  </Box>
                                </Box>
                              )}

                              {/* Feedback Form */}
                              {message.type === "feedback-form" && (
                                <Box sx={{ maxWidth: "85%", width: "100%" }}>
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      borderRadius: 2,
                                      background: "white",
                                      boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: "0.875rem",
                                        mb: 1.5,
                                      }}
                                    >
                                      {message.text}
                                    </Typography>
                                    <Box
                                      sx={{
                                        background: "#f5f5f5",
                                        p: 1.5,
                                        borderRadius: 1.5,
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        fontWeight={600}
                                        sx={{ mb: 1, display: "block" }}
                                      >
                                        Rate your experience
                                      </Typography>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          gap: 0.5,
                                          mb: 1.5,
                                        }}
                                      >
                                        {[1, 2, 3, 4, 5].map((star) => (
                                          <Box
                                            key={star}
                                            sx={{
                                              fontSize: "1.2rem",
                                              color:
                                                star <= feedbackRating
                                                  ? "#FFD700"
                                                  : "#ddd",
                                              transition: "color 0.2s",
                                            }}
                                          >
                                            ⭐
                                          </Box>
                                        ))}
                                      </Box>
                                      <Typography
                                        variant="caption"
                                        fontWeight={600}
                                        sx={{ mb: 0.5, display: "block" }}
                                      >
                                        Comments
                                      </Typography>
                                      <Box
                                        sx={{
                                          background: "white",
                                          borderRadius: 1,
                                          p: 1,
                                          minHeight: 50,
                                          fontSize: "0.75rem",
                                          color: feedbackComment
                                            ? "#000"
                                            : "#999",
                                        }}
                                      >
                                        {feedbackComment ||
                                          "Share your thoughts..."}
                                      </Box>
                                      <Button
                                        fullWidth
                                        variant="contained"
                                        size="small"
                                        sx={{
                                          mt: 1.5,
                                          background: "#075e54",
                                          textTransform: "none",
                                          fontSize: "0.75rem",
                                          py: 0.8,
                                          opacity:
                                            feedbackRating > 0 &&
                                            feedbackComment
                                              ? 1
                                              : 0.5,
                                          "&:hover": {
                                            background: "#064e45",
                                          },
                                        }}
                                      >
                                        Submit Feedback
                                      </Button>
                                    </Box>
                                  </Box>
                                </Box>
                              )}

                              {/* Feedback Thank You */}
                              {message.type === "feedback-thanks" && (
                                <Box sx={{ maxWidth: "85%", width: "100%" }}>
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      borderRadius: 2,
                                      background: "white",
                                      boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: "0.875rem",
                                        whiteSpace: "pre-line",
                                      }}
                                    >
                                      {message.text}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          </motion.div>
                        );
                      })}

                      {/* Typing Indicator */}
                      {showTyping && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          transition={{ duration: 0.2 }}
                          style={{ display: "flex" }}
                        >
                          <Box
                            sx={{
                              maxWidth: "75%",
                              p: 1,
                              borderRadius: 2,
                              background: "white",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                              display: "flex",
                              gap: 0.4,
                              alignItems: "center",
                              minWidth: 45,
                              height: 30,
                            }}
                          >
                            {[0, 1, 2].map((i) => (
                              <Box
                                key={i}
                                sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: "50%",
                                  background: "#999",
                                  animation: "typing 1.4s infinite",
                                  animationDelay: `${i * 0.2}s`,
                                  "@keyframes typing": {
                                    "0%, 60%, 100%": {
                                      transform: "translateY(0)",
                                    },
                                    "30%": { transform: "translateY(-8px)" },
                                  },
                                }}
                              />
                            ))}
                          </Box>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Box>

                  {/* UPI Payment Modal Overlay */}
                  <AnimatePresence>
                    {showUpiModal && (
                      <motion.div
                        initial={{ y: "100%" }}
                        animate={{ y: 0 }}
                        exit={{ y: "100%" }}
                        transition={{
                          type: "spring",
                          damping: 30,
                          stiffness: 300,
                        }}
                        style={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: "58%",
                          maxHeight: "400px",
                          background: "white",
                          borderTopLeftRadius: 16,
                          borderTopRightRadius: 16,
                          boxShadow: "0 -4px 20px rgba(0,0,0,0.15)",
                          zIndex: 1000,
                          overflow: "hidden",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        {/* UPI Apps Selection */}
                        {upiStep === "apps" && (
                          <Box
                            sx={{
                              p: 1.5,
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              sx={{
                                width: 32,
                                height: 3,
                                borderRadius: 2,
                                background: "#ddd",
                                mx: "auto",
                                mb: 1.5,
                              }}
                            />
                            <Typography
                              variant="h6"
                              fontWeight={600}
                              sx={{
                                mb: 1.5,
                                textAlign: "center",
                                fontSize: "0.9rem",
                              }}
                            >
                              Choose Payment App
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                                overflow: "auto",
                                px: 0.5,
                              }}
                            >
                              {upiApps.map((app) => (
                                <Box
                                  key={app.name}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    p: 1,
                                    borderRadius: 1.5,
                                    border: "1px solid #e0e0e0",
                                    cursor: "pointer",
                                    transition: "all 0.2s",
                                    "&:hover": { background: "#f5f5f5" },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      borderRadius: "50%",
                                      background: app.color,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: "white",
                                      fontWeight: 700,
                                      fontSize: "1rem",
                                      flexShrink: 0,
                                    }}
                                  >
                                    {app.logo}
                                  </Box>
                                  <Typography
                                    variant="body1"
                                    fontWeight={500}
                                    sx={{ fontSize: "0.75rem" }}
                                  >
                                    {app.name}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}

                        {/* Payment Confirmation */}
                        {upiStep === "confirm" && (
                          <Box
                            sx={{
                              p: 1.5,
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              sx={{
                                width: 50,
                                height: 50,
                                borderRadius: "50%",
                                background: upiApps.find(
                                  (a) => a.name === selectedUpiApp,
                                )?.color,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "white",
                                fontWeight: 700,
                                fontSize: "1.5rem",
                                mb: 1.5,
                              }}
                            >
                              {
                                upiApps.find((a) => a.name === selectedUpiApp)
                                  ?.logo
                              }
                            </Box>
                            <Typography
                              variant="h5"
                              fontWeight={700}
                              sx={{ mb: 0.8, fontSize: "1.25rem" }}
                            >
                              ₹30
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 0.5, fontSize: "0.8rem" }}
                            >
                              to Galaxy Mall
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.7rem" }}
                            >
                              via {selectedUpiApp}
                            </Typography>
                          </Box>
                        )}

                        {/* PIN Entry */}
                        {upiStep === "pin" && (
                          <Box
                            sx={{
                              p: 1.5,
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              overflow: "hidden",
                            }}
                          >
                            <Typography
                              variant="h6"
                              fontWeight={600}
                              sx={{ mb: 2, fontSize: "0.9rem" }}
                            >
                              Enter UPI PIN
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1.2, mb: 2 }}>
                              {[0, 1, 2, 3].map((i) => (
                                <Box
                                  key={i}
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: "50%",
                                    border: "2px solid #000",
                                    background: pinDigits[i]
                                      ? "#000"
                                      : "transparent",
                                    transition: "all 0.2s",
                                  }}
                                />
                              ))}
                            </Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.7rem" }}
                            >
                              Secured by {selectedUpiApp}
                            </Typography>
                          </Box>
                        )}

                        {/* Success Animation */}
                        {upiStep === "success" && (
                          <Box
                            sx={{
                              p: 1.5,
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              overflow: "hidden",
                            }}
                          >
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{
                                type: "spring",
                                damping: 10,
                                stiffness: 200,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 60,
                                  height: 60,
                                  borderRadius: "50%",
                                  background: "#28a745",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  mb: 1.5,
                                }}
                              >
                                <CheckCircle
                                  sx={{ fontSize: 36, color: "white" }}
                                />
                              </Box>
                            </motion.div>
                            <Typography
                              variant="h5"
                              fontWeight={700}
                              sx={{
                                mb: 0.8,
                                color: "#28a745",
                                fontSize: "1.1rem",
                              }}
                            >
                              Payment Successful!
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontSize: "0.8rem" }}
                            >
                              ₹30 paid to Galaxy Mall
                            </Typography>
                          </Box>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* MallHub Full Screen View */}
                  <AnimatePresence>
                    {showMallHub && (
                      <motion.div
                        initial={{ y: "100%" }}
                        animate={{ y: 0 }}
                        exit={{ y: "100%" }}
                        transition={{
                          type: "spring",
                          damping: 30,
                          stiffness: 300,
                        }}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: "white",
                          zIndex: 1200,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        {/* MallHub Header */}
                        <Box
                          sx={{
                            background: gradients.gold,
                            p: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            sx={{ color: "#1a237e", fontSize: "0.8rem" }}
                          >
                            Explore Galaxy Mall Digitally
                          </Typography>
                        </Box>

                        {/* Search Bar */}
                        <Box
                          sx={{
                            p: 1.5,
                            background: "white",
                            borderBottom: "1px solid #e0e0e0",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              p: 1,
                              borderRadius: 2,
                              background: "#f5f5f5",
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            <Search sx={{ fontSize: 18, color: "#666" }} />
                            <Typography
                              variant="body2"
                              sx={{ color: "#999", fontSize: "0.8rem" }}
                            >
                              Search Brands, Offers...
                            </Typography>
                          </Box>
                        </Box>

                        {/* MallHub Content - Scrollable */}
                        <Box
                          ref={mallHubRef}
                          sx={{
                            flex: 1,
                            overflowY: "auto",
                            overflowX: "hidden",
                            "&::-webkit-scrollbar": { width: 3 },
                            "&::-webkit-scrollbar-thumb": {
                              background: "rgba(0,0,0,0.2)",
                              borderRadius: 2,
                            },
                          }}
                        >
                          {/* Hero Banner */}
                          <Box
                            sx={{
                              background: `linear-gradient(135deg, #1a237e 0%, #3949ab 100%)`,
                              p: 1.5,
                              color: "white",
                            }}
                          >
                            <Typography
                              variant="h6"
                              fontWeight={700}
                              sx={{ fontSize: "0.9rem", mb: 0.5 }}
                            >
                              Galaxy Mall
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                opacity: 0.9,
                                display: "block",
                                mb: 1,
                                fontSize: "0.65rem",
                              }}
                            >
                              Pune, Maharashtra • Open Now
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 0.5,
                                flexWrap: "wrap",
                              }}
                            >
                              <Chip
                                label="120+ Stores"
                                size="small"
                                sx={{
                                  background: "rgba(255,255,255,0.2)",
                                  color: "white",
                                  fontSize: "0.6rem",
                                  height: 20,
                                }}
                              />
                              <Chip
                                label="25 Offers"
                                size="small"
                                sx={{
                                  background: "rgba(255,255,255,0.2)",
                                  color: "white",
                                  fontSize: "0.6rem",
                                  height: 20,
                                }}
                              />
                              <Chip
                                label="5 Events"
                                size="small"
                                sx={{
                                  background: "rgba(255,255,255,0.2)",
                                  color: "white",
                                  fontSize: "0.6rem",
                                  height: 20,
                                }}
                              />
                            </Box>
                          </Box>

                          {/* Quick Stats */}
                          <Box sx={{ display: "flex", p: 1.5, gap: 0.8 }}>
                            <Box
                              sx={{
                                flex: 1,
                                textAlign: "center",
                                p: 1,
                                borderRadius: 1.5,
                                background: "#fff3e0",
                              }}
                            >
                              <TrendingUp
                                sx={{ fontSize: 20, color: "#f57c00", mb: 0.3 }}
                              />
                              <Typography
                                variant="h6"
                                sx={{
                                  fontSize: "0.85rem",
                                  fontWeight: 700,
                                  color: "#f57c00",
                                }}
                              >
                                25
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: "#666", fontSize: "0.6rem" }}
                              >
                                Hot Offers
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                flex: 1,
                                textAlign: "center",
                                p: 1,
                                borderRadius: 1.5,
                                background: "#e8f5e9",
                              }}
                            >
                              <Store
                                sx={{ fontSize: 20, color: "#2e7d32", mb: 0.3 }}
                              />
                              <Typography
                                variant="h6"
                                sx={{
                                  fontSize: "0.85rem",
                                  fontWeight: 700,
                                  color: "#2e7d32",
                                }}
                              >
                                120+
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: "#666", fontSize: "0.6rem" }}
                              >
                                Stores
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                flex: 1,
                                textAlign: "center",
                                p: 1,
                                borderRadius: 1.5,
                                background: "#f3e5f5",
                              }}
                            >
                              <Event
                                sx={{ fontSize: 20, color: "#7b1fa2", mb: 0.3 }}
                              />
                              <Typography
                                variant="h6"
                                sx={{
                                  fontSize: "0.85rem",
                                  fontWeight: 700,
                                  color: "#7b1fa2",
                                }}
                              >
                                5
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: "#666", fontSize: "0.6rem" }}
                              >
                                Events
                              </Typography>
                            </Box>
                          </Box>

                          {/* Featured Offers Carousel */}
                          <Box sx={{ py: 1.5, background: "white" }}>
                            <Box
                              sx={{
                                px: 1.5,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                fontWeight={600}
                                sx={{ fontSize: "0.8rem" }}
                              >
                                🔥 Featured Offers
                              </Typography>
                              <Button
                                size="small"
                                endIcon={<ChevronRight sx={{ fontSize: 14 }} />}
                                sx={{
                                  fontSize: "0.65rem",
                                  textTransform: "none",
                                  color: "#1a237e",
                                  minWidth: "auto",
                                  p: 0.5,
                                }}
                              >
                                View All
                              </Button>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                overflowX: "auto",
                                px: 1.5,
                                scrollBehavior: "smooth",
                                "&::-webkit-scrollbar": { display: "none" },
                                msOverflowStyle: "none",
                                scrollbarWidth: "none",
                              }}
                            >
                              {[1, 2, 3].map((i) => (
                                <Box
                                  key={i}
                                  sx={{
                                    minWidth: 200,
                                    p: 1,
                                    borderRadius: 1.5,
                                    background: `linear-gradient(135deg, ${
                                      i === 1
                                        ? "#1a237e"
                                        : i === 2
                                          ? "#fa709a"
                                          : "#4facfe"
                                    } 0%, ${
                                      i === 1
                                        ? "#3949ab"
                                        : i === 2
                                          ? "#fee140"
                                          : "#00f2fe"
                                    } 100%)`,
                                    color: "white",
                                    display: "flex",
                                    flexDirection: "column",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      mb: 0.8,
                                    }}
                                  >
                                    <LocalOffer sx={{ fontSize: 20 }} />
                                    <Typography
                                      variant="subtitle2"
                                      fontWeight={700}
                                      sx={{ fontSize: "0.75rem" }}
                                    >
                                      {i === 1
                                        ? "50% OFF"
                                        : i === 2
                                          ? "BOGO"
                                          : "Weekend"}
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: "0.7rem",
                                      mb: 0.5,
                                      opacity: 0.95,
                                    }}
                                  >
                                    {i === 1
                                      ? "SportWear Co"
                                      : i === 2
                                        ? "Fashion Hub"
                                        : "Burger Spot"}
                                  </Typography>
                                  <Chip
                                    label={
                                      i === 1
                                        ? "Fashion"
                                        : i === 2
                                          ? "Apparel"
                                          : "Food"
                                    }
                                    size="small"
                                    sx={{
                                      height: 16,
                                      fontSize: "0.55rem",
                                      background: "rgba(255,255,255,0.25)",
                                      color: "white",
                                      alignSelf: "flex-start",
                                    }}
                                  />
                                </Box>
                              ))}
                            </Box>
                          </Box>

                          {/* Upcoming Events Carousel */}
                          <Box sx={{ py: 1.5, background: "#f8f9fa" }}>
                            <Box
                              sx={{
                                px: 1.5,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                fontWeight={600}
                                sx={{ fontSize: "0.8rem" }}
                              >
                                🎉 Upcoming Events
                              </Typography>
                              <Button
                                size="small"
                                endIcon={<ChevronRight sx={{ fontSize: 14 }} />}
                                sx={{
                                  fontSize: "0.65rem",
                                  textTransform: "none",
                                  color: "#1a237e",
                                  minWidth: "auto",
                                  p: 0.5,
                                }}
                              >
                                View All
                              </Button>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                overflowX: "auto",
                                px: 1.5,
                                scrollBehavior: "smooth",
                                "&::-webkit-scrollbar": { display: "none" },
                                msOverflowStyle: "none",
                                scrollbarWidth: "none",
                              }}
                            >
                              {[
                                "Fashion Show",
                                "Food Festival",
                                "Live Music",
                              ].map((event, i) => (
                                <Box
                                  key={event}
                                  sx={{
                                    minWidth: 180,
                                    p: 1.2,
                                    borderRadius: 1.5,
                                    background: `linear-gradient(135deg, ${
                                      i === 0
                                        ? "#f093fb"
                                        : i === 1
                                          ? "#4facfe"
                                          : "#fa709a"
                                    } 0%, ${
                                      i === 0
                                        ? "#f5576c"
                                        : i === 1
                                          ? "#00f2fe"
                                          : "#fee140"
                                    } 100%)`,
                                    color: "white",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.8,
                                      mb: 0.8,
                                    }}
                                  >
                                    <Event sx={{ fontSize: 18 }} />
                                    <Typography
                                      variant="subtitle2"
                                      fontWeight={700}
                                      sx={{ fontSize: "0.75rem" }}
                                    >
                                      {event}
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant="caption"
                                    sx={{ opacity: 0.95, fontSize: "0.65rem" }}
                                  >
                                    {i === 0
                                      ? "Tomorrow, 6 PM"
                                      : i === 1
                                        ? "This Weekend"
                                        : "Friday, 7 PM"}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>

                          {/* Popular Stores Carousel */}
                          <Box sx={{ py: 1.5, background: "white" }}>
                            <Box
                              sx={{
                                px: 1.5,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                fontWeight={600}
                                sx={{ fontSize: "0.8rem" }}
                              >
                                ⭐ Popular Stores
                              </Typography>
                              <Button
                                size="small"
                                endIcon={<ChevronRight sx={{ fontSize: 14 }} />}
                                sx={{
                                  fontSize: "0.65rem",
                                  textTransform: "none",
                                  color: "#1a237e",
                                  minWidth: "auto",
                                  p: 0.5,
                                }}
                              >
                                View All
                              </Button>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                overflowX: "auto",
                                px: 1.5,
                                pb: 1,
                                scrollBehavior: "smooth",
                                "&::-webkit-scrollbar": { display: "none" },
                                msOverflowStyle: "none",
                                scrollbarWidth: "none",
                              }}
                            >
                              {[
                                {
                                  name: "SportWear Co",
                                  icon: SportsBasketball,
                                  color: "#1a237e",
                                },
                                {
                                  name: "Fashion Hub",
                                  icon: Checkroom,
                                  color: "#fa709a",
                                },
                                {
                                  name: "Coffee Corner",
                                  icon: LocalCafe,
                                  color: "#4facfe",
                                },
                              ].map((store) => (
                                <Box
                                  key={store.name}
                                  sx={{
                                    minWidth: 90,
                                    p: 1,
                                    borderRadius: 1.5,
                                    background: "#f5f5f5",
                                    border: "1px solid #e0e0e0",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 0.8,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 36,
                                      height: 36,
                                      borderRadius: "50%",
                                      background: store.color,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: "white",
                                    }}
                                  >
                                    <store.icon sx={{ fontSize: 20 }} />
                                  </Box>
                                  <Typography
                                    variant="caption"
                                    fontWeight={600}
                                    sx={{
                                      fontSize: "0.65rem",
                                      textAlign: "center",
                                    }}
                                  >
                                    {store.name}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>

                          {/* Start Exploring Today Footer */}
                          <Box
                            sx={{
                              background: `linear-gradient(135deg, #1a237e 0%, #3949ab 100%)`,
                              color: "white",
                              p: 3,
                              textAlign: "center",
                              mt: 2,
                            }}
                          >
                            <Typography
                              variant="h6"
                              fontWeight={700}
                              sx={{ fontSize: "1rem", mb: 1 }}
                            >
                              Start Exploring Today
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                mb: 2,
                                opacity: 0.9,
                                fontSize: "0.7rem",
                              }}
                            >
                              Discover amazing deals, exciting events, and your
                              favorite stores all in one place!
                            </Typography>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => {}}
                              sx={{
                                background: "white",
                                color: "#1a237e",
                                fontWeight: 600,
                                px: 3,
                                py: 0.8,
                                fontSize: "0.75rem",
                                textTransform: "none",
                                borderRadius: 2,
                                transition: "all 0.2s ease",
                                "&:hover": {
                                  background: "rgba(255,255,255,0.9)",
                                },
                                "&:active": {
                                  transform: "scale(0.98)",
                                },
                              }}
                            >
                              Explore Now
                            </Button>
                          </Box>
                        </Box>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Box>
              </Box>
            </Box>
          </AnimatedSection>
        </Box>
      </Container>
    </Box>
  );
};

export default WhatsAppChatPreview;
